/**
 * Icescape Scene — ringed moon in a dark night sky, falling snow,
 * twinkling stars, icy atmosphere.
 */

import { Graphics } from 'pixi.js';
import type { StageScene, ParallaxLayers } from '../stage-scene';
import type { StageTheme } from '../../../stages';
import { WORLD } from '../../../constants';
import { Star } from '../particles/star';
import { FallingParticle } from '../particles/falling-particle';

interface RingSegment { gfx: Graphics; baseAlpha: number; phase: number }

export class IcescapeScene implements StageScene {
  private stars: Star[] = [];
  private snowflakes: FallingParticle[] = [];
  private ringSegments: RingSegment[] = [];
  private moonGlow: Graphics | null = null;
  private layers: ParallaxLayers | null = null;

  build(layers: ParallaxLayers, theme: StageTheme): void {
    this.layers = layers;
    const W = WORLD.VIRTUAL_WIDTH;
    const H = WORLD.VIRTUAL_HEIGHT;

    // ── Sky Layer: Night sky + stars ──

    // Dark cold sky
    const skyBg = new Graphics();
    skyBg.rect(0, 0, W, H);
    skyBg.fill({ color: 0x020408, alpha: 1.0 });
    layers.sky.container.addChild(skyBg);

    // Dense starfield (250 stars, upper 55%)
    for (let i = 0; i < 250; i++) {
      const x = Math.random() * W;
      const y = Math.random() * H * 0.55;
      const size = 0.3 + Math.random() * 1.5;
      const color = theme.starColors[Math.floor(Math.random() * theme.starColors.length)];
      const baseAlpha = 0.15 + Math.random() * 0.7;

      this.stars.push(new Star(layers.sky.container, {
        x, y, size, color, baseAlpha,
        twinkleSpeed: 1.5 + Math.random() * 3,
      }));
    }

    // ── Deep Layer: Ringed Moon ──
    const moonX = W * 0.6;
    const moonY = H * 0.18;
    const moonR = 65;

    // Moon outer glow (atmospheric haze)
    this.moonGlow = new Graphics();
    this.moonGlow.circle(moonX, moonY, moonR + 60);
    this.moonGlow.fill({ color: 0x8899bb, alpha: 0.06 });
    this.moonGlow.circle(moonX, moonY, moonR + 30);
    this.moonGlow.fill({ color: 0xaabbcc, alpha: 0.08 });
    layers.deep.container.addChild(this.moonGlow);

    // Moon body
    const moonBody = new Graphics();
    moonBody.circle(moonX, moonY, moonR);
    moonBody.fill({ color: 0xccddee, alpha: 0.25 });
    // Lighter crescent highlight
    moonBody.circle(moonX - 10, moonY - 8, moonR * 0.85);
    moonBody.fill({ color: 0xddeeff, alpha: 0.1 });
    layers.deep.container.addChild(moonBody);

    // Saturn-like rings (drawn as elliptical arcs)
    // Ring behind moon (lower layer)
    const ringTilt = 0.25;
    const ringColors = [0xaabbdd, 0x8899cc, 0xbbccee, 0x7788aa];
    const ringWidths = [140, 120, 100, 85];
    const ringHeights = [22, 18, 15, 12];

    // Back half of rings (behind moon)
    for (let r = 0; r < ringColors.length; r++) {
      const rGfx = new Graphics();
      // Draw full ellipse but we'll render it with low alpha to simulate being behind the moon
      rGfx.ellipse(moonX, moonY + ringTilt * 10, ringWidths[r], ringHeights[r]);
      rGfx.stroke({ color: ringColors[r], alpha: 0.08 + r * 0.02, width: 2 - r * 0.3 });
      layers.deep.container.addChild(rGfx);
      this.ringSegments.push({ gfx: rGfx, baseAlpha: 0.08 + r * 0.02, phase: r * 0.8 });
    }

    // Front half of rings (in front of moon) — brighter
    for (let r = 0; r < 3; r++) {
      const rGfx = new Graphics();
      // Just the front arc segments (left and right sides visible past moon edge)
      const rw = ringWidths[r];
      const rh = ringHeights[r];
      const ry = moonY + ringTilt * 10;

      // Left visible segment
      for (let a = Math.PI * 0.55; a <= Math.PI; a += 0.05) {
        const px = moonX + Math.cos(a) * rw;
        const py = ry + Math.sin(a) * rh;
        rGfx.circle(px, py, 0.8);
      }
      // Right visible segment
      for (let a = 0; a <= Math.PI * 0.45; a += 0.05) {
        const px = moonX + Math.cos(a) * rw;
        const py = ry + Math.sin(a) * rh;
        rGfx.circle(px, py, 0.8);
      }
      rGfx.fill({ color: ringColors[r], alpha: 0.2 + r * 0.03 });
      layers.deep.container.addChild(rGfx);
      this.ringSegments.push({ gfx: rGfx, baseAlpha: 0.2 + r * 0.03, phase: r * 1.2 });
    }

    // Icy horizon glow
    const horizonGlow = new Graphics();
    horizonGlow.ellipse(W * 0.5, H * 0.65, W * 0.6, 80);
    horizonGlow.fill({ color: 0x223344, alpha: 0.08 });
    layers.deep.container.addChild(horizonGlow);

    // Distant mountain ridges (icy)
    for (let layer = 0; layer < 2; layer++) {
      const mtn = new Graphics();
      mtn.moveTo(0, H);
      const baseY = H * (0.55 + layer * 0.08);
      const segments = 25;
      for (let s = 0; s <= segments; s++) {
        const x = (s / segments) * W;
        const peakOffset = Math.sin(s * 0.7 + layer * 3) * 50 + Math.sin(s * 1.8 + layer) * 25;
        mtn.lineTo(x, baseY + peakOffset);
      }
      mtn.lineTo(W, H);
      mtn.closePath();
      mtn.fill({ color: layer === 0 ? 0x0a1018 : 0x060c12, alpha: 0.6 });
      layers.deep.container.addChild(mtn);
    }

    // ── Mid Layer: Snowfall ──

    // 150 snowflakes — always falling
    for (let i = 0; i < 150; i++) {
      const x = Math.random() * W;
      const y = Math.random() * H;
      const size = 0.5 + Math.random() * 2;
      const alpha = 0.15 + Math.random() * 0.4;

      this.snowflakes.push(new FallingParticle(layers.mid.container, {
        x, y, size, alpha, color: 0xeeeeff,
        speed: 20 + Math.random() * 40,
        drift: 5 + Math.random() * 15,
        driftPhase: Math.random() * Math.PI * 2,
      }));
    }
  }

  update(dt: number, elapsed: number): void {
    const W = WORLD.VIRTUAL_WIDTH;
    const H = WORLD.VIRTUAL_HEIGHT;

    // Star twinkle
    for (const star of this.stars) {
      star.update(elapsed);
    }

    // Moon glow pulse (very subtle)
    if (this.moonGlow) {
      this.moonGlow.alpha = 0.85 + 0.15 * Math.sin(elapsed * 0.4);
    }

    // Ring shimmer
    for (const seg of this.ringSegments) {
      seg.gfx.alpha = seg.baseAlpha * (0.8 + 0.2 * Math.sin(elapsed * 0.6 + seg.phase));
    }

    // Snowfall — drift down with lateral sway
    for (const flake of this.snowflakes) {
      flake.update(dt, elapsed, W, H);
    }
  }

  onExplosion(_worldX: number, _worldY: number, radius: number): void {
    // Moon brightens momentarily
    if (this.moonGlow) {
      this.moonGlow.alpha = Math.min(1, 0.9 + 0.2 * (radius / 80));
    }
    // Brief snowflake scatter — increase drift temporarily
    for (const flake of this.snowflakes) {
      flake.driftPhase += (Math.random() - 0.5) * 2;
    }
  }

  destroy(): void {
    this.stars = [];
    this.snowflakes = [];
    this.ringSegments = [];
    this.moonGlow = null;
    if (this.layers) {
      this.layers.sky.clear();
      this.layers.deep.clear();
      this.layers.mid.clear();
    }
  }
}
